// ./src/components/features/TireSets/TireSetFilters/TireSetFilters.tsx
//
// Tire-set-specific filters wrapper around ExpenseFiltersToolbar.
// Adds tire type and status filters as extraFilters (in drawer)
// and extraChips (in the active filters row).
//
// Draft state pattern:
// - Maintains draft values for tireType and status internally.
// - Exposes an ExtraFiltersHandle via ref so ExpenseFiltersDrawer can
//   call onOpen (sync applied → draft), onApply (commit draft → applied),
//   onClear (reset drafts), and hasDraftValues (for Clear button visibility).
// - The extraFilters rendered in the drawer bind to draft values, so
//   selecting a tire type or status does NOT trigger a data reload until
//   the user clicks Apply.
// - extraChips bind to the APPLIED values (from props), so chips only
//   appear after Apply is clicked.

import React, { useCallback, useMemo, useRef, useState } from "react";
import { Badge, CloseButton, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { ExpenseFiltersToolbar } from "@/components/features";
import { ExtraFiltersHandle } from "@/components/features/ExpenseFiltersToolbar/ExpenseFiltersDrawer";
import { TireTypeSelector } from "@/components/generic/TireTypeSelector/TireTypeSelector";
import { TireSetStatusSelector } from "@/components/generic/TireSetStatusSelector/TireSetStatusSelector";
import {
  TIRE_TYPE_MAP,
  TIRE_SET_STATUS_MAP,
} from "@/config/tireOptions";
import { TireTypeValue } from "@/utils/api";

// ============================================
// Types
// ============================================

export interface TireSetFiltersState {
  /** Selected tire type (null = all) */
  tireType: TireTypeValue | null;
  /** Selected status as string (null = all) */
  status: string | null;
}

export interface TireSetFiltersProps {
  /** Handler for add button click */
  onAdd?: () => void;

  /** Handler for refresh button click */
  onRefresh?: () => void;

  /** Whether add button is disabled */
  addDisabled?: boolean;

  /** Whether refresh button is disabled */
  refreshDisabled?: boolean;

  /** Whether data is loading */
  loading?: boolean;

  /** Custom label for add button */
  addLabel?: string;

  /** Current applied tire type filter value */
  tireType?: TireTypeValue | null;

  /** Callback when tire type is applied */
  onTireTypeChange?: (value: TireTypeValue | null) => void;

  /** Current applied status filter value */
  status?: string | null;

  /** Callback when status is applied */
  onStatusChange?: (value: string | null) => void;

  /** Enable keyboard shortcuts (default: true) */
  enableShortcuts?: boolean;

  /**
   * Callback emitting a combined filter object built from all enabled sources.
   * Passed through to ExpenseFiltersToolbar.
   */
  onFiltersChange?: (filters: Record<string, unknown> | null) => void;
}

// ============================================
// Component
// ============================================

const TireSetFiltersComponent: React.FC<TireSetFiltersProps> = ({
  onAdd,
  onRefresh,
  addDisabled = false,
  refreshDisabled = false,
  loading = false,
  addLabel,
  tireType = null,
  onTireTypeChange,
  status = null,
  onStatusChange,
  enableShortcuts = true,
  onFiltersChange,
}) => {
  const { t } = useTranslation(["tireSets", "common"]);

  // ============================================
  // Draft State (used inside the drawer only)
  // ============================================

  const [draftTireType, setDraftTireType] = useState<TireTypeValue | null>(null);
  const [draftStatus, setDraftStatus] = useState<string | null>(null);

  // Refs to track latest applied values for getValues
  const tireTypeRef = useRef(tireType);
  tireTypeRef.current = tireType;
  const statusRef = useRef(status);
  statusRef.current = status;

  // ============================================
  // ExtraFiltersHandle — lifecycle contract with ExpenseFiltersDrawer
  // ============================================

  const extraFiltersRef = useRef<ExtraFiltersHandle>({
    onOpen: () => {
      setDraftTireType(tireType);
      setDraftStatus(status);
    },
    onApply: () => {
      onTireTypeChange?.(draftTireType);
      onStatusChange?.(draftStatus);
      // Return values being applied so the toolbar can include them
      // in onFiltersChange synchronously
      const values: Record<string, unknown> = {};
      if (draftTireType !== null) {
        values.tireType = draftTireType;
      }
      if (draftStatus !== null) {
        values.status = draftStatus;
      }
      return values;
    },
    onClear: () => {
      setDraftTireType(null);
      setDraftStatus(null);
    },
    hasDraftValues: () => {
      return draftTireType !== null || draftStatus !== null;
    },
    getValues: () => {
      // Return current applied (not draft) values for non-Apply emit paths
      const values: Record<string, unknown> = {};
      if (tireTypeRef.current !== null) {
        values.tireType = tireTypeRef.current;
      }
      if (statusRef.current !== null) {
        values.status = statusRef.current;
      }
      return values;
    },
  });

  // Keep the ref callbacks up to date with latest state/props
  extraFiltersRef.current.onOpen = () => {
    setDraftTireType(tireType);
    setDraftStatus(status);
  };
  extraFiltersRef.current.onApply = () => {
    onTireTypeChange?.(draftTireType);
    onStatusChange?.(draftStatus);
    const values: Record<string, unknown> = {};
    if (draftTireType !== null) {
      values.tireType = draftTireType;
    }
    if (draftStatus !== null) {
      values.status = draftStatus;
    }
    return values;
  };
  extraFiltersRef.current.onClear = () => {
    setDraftTireType(null);
    setDraftStatus(null);
  };
  extraFiltersRef.current.hasDraftValues = () => {
    return draftTireType !== null || draftStatus !== null;
  };
  extraFiltersRef.current.getValues = () => {
    const values: Record<string, unknown> = {};
    if (tireTypeRef.current !== null) {
      values.tireType = tireTypeRef.current;
    }
    if (statusRef.current !== null) {
      values.status = statusRef.current;
    }
    return values;
  };

  // ============================================
  // Handlers — Draft changes (inside drawer)
  // ============================================

  const handleDraftTireTypeChange = useCallback(
    (_name: string | undefined, value: string | null | undefined) => {
      setDraftTireType((value as TireTypeValue) || null);
    },
    []
  );

  const handleDraftStatusChange = useCallback(
    (_name: string | undefined, value: string | null | undefined) => {
      setDraftStatus(value || null);
    },
    []
  );

  // ============================================
  // Handlers — Chip removal (removes applied values directly)
  // ============================================

  const handleRemoveTireType = useCallback(() => {
    onTireTypeChange?.(null);
  }, [onTireTypeChange]);

  const handleRemoveStatus = useCallback(() => {
    onStatusChange?.(null);
  }, [onStatusChange]);

  // ============================================
  // Build Extra Chips (based on APPLIED values, not drafts)
  // ============================================

  const extraChips = useMemo(() => {
    const chips: React.ReactNode[] = [];

    // Tire type chip
    if (tireType) {
      const config = TIRE_TYPE_MAP[tireType];
      if (config) {
        const label = t(`tireSets:${config.labelKey}`);
        chips.push(
          <Badge
            key="tire-type"
            variant="light"
            color={config.color}
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={handleRemoveTireType}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {label}
          </Badge>
        );
      }
    }

    // Status chip
    if (status) {
      const config = TIRE_SET_STATUS_MAP[status];
      if (config) {
        const label = t(`tireSets:${config.labelKey}`);
        chips.push(
          <Badge
            key="tire-status"
            variant="light"
            color={config.color}
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={handleRemoveStatus}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {label}
          </Badge>
        );
      }
    }

    if (chips.length === 0) return undefined;
    return <>{chips}</>;
  }, [tireType, status, handleRemoveTireType, handleRemoveStatus, t]);

  // ============================================
  // Build Extra Filters Content (for drawer — uses DRAFT values)
  // ============================================

  const extraFilters = useMemo(() => {
    return (
      <Stack gap="md">
        <TireTypeSelector
          value={draftTireType}
          onChange={handleDraftTireTypeChange}
          label={t("tireSets:filters.tireType", { defaultValue: "Tire Type" })}
          placeholder={t("tireSets:filters.allTypes")}
          clearable
          size="md"
          comboboxProps={{ withinPortal: false }}
        />
        <TireSetStatusSelector
          value={draftStatus}
          onChange={handleDraftStatusChange}
          label={t("tireSets:filters.status", { defaultValue: "Status" })}
          placeholder={t("tireSets:filters.allStatuses")}
          clearable
          size="md"
          comboboxProps={{ withinPortal: false }}
        />
      </Stack>
    );
  }, [draftTireType, draftStatus, handleDraftTireTypeChange, handleDraftStatusChange, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <ExpenseFiltersToolbar
      entityType="tire-sets"
      onAdd={onAdd}
      onRefresh={onRefresh}
      addDisabled={addDisabled}
      refreshDisabled={refreshDisabled}
      loading={loading}
      addLabel={addLabel}
      carFilterMode="single"
      showDateFilter={false}
      showTagFilter={false}
      enableShortcuts={enableShortcuts}
      extraFilters={extraFilters}
      extraChips={extraChips}
      extraFiltersRef={extraFiltersRef}
      onFiltersChange={onFiltersChange}
    />
  );
};

export const TireSetFilters = React.memo(TireSetFiltersComponent);